import {LocalizationProvider} from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import {Autocomplete, Grid, InputAdornment, MenuItem} from '@mui/material';
import {AchRejectCodes, emptyStringToNull, ListOption} from '@ozark/common';
import {Fragment} from 'react';
import {UseFormReturn} from 'react-hook-form';
import * as yup from 'yup';
import {useAuthorizedMids} from '../../../hooks/useAuthorizedMids';
import {Loading} from '../../Loading';
import {Select} from '../../Select';
import {TextField} from '../../TextField';

const TransactionCodes: ListOption[] = [
  {key: '22', value: '22: Credit'},
  {key: '32', value: '32: Credit'},
  {key: '27', value: '27: Debit'},
  {key: '37', value: '37: Debit'},
];

const RejectCollectionStatuses: ListOption[] = [
  {key: 'New', value: 'New'},
  {key: 'In Progress', value: 'In Progress'},
  {key: 'Resolved', value: 'Resolved'},
  {key: 'Sent to Collections', value: 'Sent to Collections'},
];

export const RejectCollectionMethods: ListOption[] = [
  {key: 'Collected from Divert', value: 'Collected from Divert'},
  {key: 'Collected from Merchant Reserves', value: 'Collected from Merchant Reserves'},
  {
    key: 'Other Methods of Collections(MDE, Wires, Etc.)',
    value: 'Other Methods of Collections(MDE, Wires, Etc.)',
  },
];

export type RejectFormData = {
  mid: string;
  transactionCode: string;
  rejectDate: Date;
  rejectCode: string;
  amountApplied: number;
  rejectFeeApplied: number;
  collectionStatus?: string;
  collectionMethod?: string;
  amountOfPaymentApplied?: number;
  totalAdminFee?: number;
  divertAmount?: number;
  notes?: string;
};

export const RejectFormSchema = yup.object().shape({
  mid: yup
    .string()
    .transform(emptyStringToNull)
    .nullable(false)
    .required()
    .typeError('MID is required'),
  rejectDate: yup.date().required(),
  rejectCode: yup
    .string()
    .transform(emptyStringToNull)
    .nullable(false)
    .required()
    .typeError('Reject Code is required'),
  transactionCode: yup
    .string()
    .transform(emptyStringToNull)
    .nullable(false)
    .required()
    .typeError('Transaction Code is required'),
  amountApplied: yup.number().required().typeError('Amount is required'),
  rejectFeeApplied: yup.number().required().typeError('Reject Fee is required'),
  collectionStatus: yup.string().transform(emptyStringToNull).nullable(),
  collectionMethod: yup.string().transform(emptyStringToNull).nullable(),
  amountOfPaymentApplied: yup.number().transform(emptyStringToNull).nullable(),
  totalAdminFee: yup.number().transform(emptyStringToNull).nullable(),
  divertAmount: yup.number().transform(emptyStringToNull).nullable(),
  notes: yup.string().transform(emptyStringToNull).nullable(),
});

type Props = {
  readOnly: boolean;
  isEditMode: boolean;
  hookForm: UseFormReturn<RejectFormData>;
};

export const RejectForm = ({readOnly, isEditMode, hookForm}: Props) => {
  const {authorizedMids} = useAuthorizedMids();

  const {
    watch,
    formState: {errors},
    control,
    setValue,
  } = hookForm;

  if (!isEditMode && authorizedMids.promised) return <Loading />;

  return (
    <Fragment>
      {!isEditMode && (
        <>
          <Grid item xs={12}>
            <Autocomplete
              onChange={async (_: React.SyntheticEvent<Element, Event>, newValue: any) => {
                setValue('mid', newValue?.mid || '', {shouldDirty: true});
              }}
              options={authorizedMids.data || []}
              getOptionLabel={(x: any) => `${x.dba} (${x.mid})`}
              renderInput={params => (
                <TextField
                  {...params}
                  name="mid"
                  label="MID"
                  required
                  disabled={readOnly}
                  errors={errors}
                  control={control}
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                inputFormat={'MM/dd/yyyy'}
                value={watch('rejectDate')}
                label="Year and Month"
                onChange={(date: Date | null) =>
                  setValue('rejectDate', date || new Date(), {shouldDirty: true})
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    name="rejectDate"
                    label="Reject Date"
                    disabled={readOnly}
                    errors={errors}
                    control={control}
                    variant="outlined"
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </>
      )}
      <Grid item xs={isEditMode ? 6 : 3}>
        <TextField
          name="amountApplied"
          label="Amount"
          required
          errors={errors}
          control={control}
          disabled={readOnly}
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={isEditMode ? 6 : 3}>
        <TextField
          name="rejectFeeApplied"
          label="Reject Fee"
          required
          errors={errors}
          control={control}
          disabled={readOnly}
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </Grid>

      {!isEditMode && (
        <Grid item xs={6}>
          <Select
            name="transactionCode"
            label="Transaction Code"
            required
            errors={errors}
            control={control}
            disabled={readOnly}
          >
            {TransactionCodes.map(x => (
              <MenuItem key={`${x.key}`} value={x.key}>
                {x.value}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      )}
      <Grid item xs={4}>
        <TextField
          name="totalAdminFee"
          label="Total Admin Fee"
          errors={errors}
          control={control}
          disabled={readOnly}
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          name="amountOfPaymentApplied"
          label="Amount Of Payment Applied"
          errors={errors}
          control={control}
          disabled={readOnly}
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          name="divertAmount"
          label="Full Divert/Credit Amount"
          errors={errors}
          control={control}
          disabled={readOnly}
          type="number"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Select
          name="rejectCode"
          label="Reject Code"
          required
          errors={errors}
          control={control}
          disabled={readOnly}
        >
          {AchRejectCodes.map(x => (
            <MenuItem key={`${x.code}`} value={x.code}>
              {x.code}: {x.description}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12}>
        <Select
          name="collectionStatus"
          label="Collection Status"
          errors={errors}
          control={control}
          disabled={readOnly}
        >
          {RejectCollectionStatuses.map(x => (
            <MenuItem key={`${x.key}`} value={x.key}>
              {x.key}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12}>
        <Select
          name="collectionMethod"
          label="Collection Method"
          errors={errors}
          control={control}
          disabled={readOnly}
        >
          {RejectCollectionMethods.map(x => (
            <MenuItem key={`${x.key}`} value={x.key}>
              {x.key}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12}>
        <TextField
          name="notes"
          label="Notes"
          variant="outlined"
          errors={errors}
          control={control}
          disabled={readOnly}
          multiLine
          minRows={2}
          maxRows={4}
          fullWidth
        />
      </Grid>
    </Fragment>
  );
};
